import { useState } from "react";
import { ArrowDown, ArrowUp, ListFilter, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// No arrow at rest; a faint one on hover; the ruling column shows its direction.
export const SortArrow = ({ sortActive, direction }) => (sortActive
  ? (direction === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
  : <ArrowDown className="h-3 w-3 opacity-0 transition-opacity group-hover/th:opacity-40" />);

// Small filter menu that lives in the column header. options: [{ value, label, count }]. `value` is a string (single —
// the menu closes on pick) or an array (multi — the menu stays open). "All" clears it.
export function HeaderFilter({ label, options, value, onChange, multi = false, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = multi ? (Array.isArray(value) ? value : []) : value;
  const active = multi ? selected.length > 0 : !!selected;
  const toggle = (v) => {
    if (!multi) { onChange(v === selected ? "" : v); setOpen(false); return; }
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };
  const clear = () => { onChange(multi ? [] : ""); if (!multi) setOpen(false); };
  const shown = q.trim() ? options.filter((o) => String(o.label).toLowerCase().includes(q.trim().toLowerCase())) : options;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <button type="button" title={`Filter ${label}`} onClick={(e) => e.stopPropagation()} data-testid={testId}
          className={`rounded p-0.5 hover:bg-slate-100 ${active ? "text-brand" : "text-slate-400 hover:text-slate-700"}`}>
          <ListFilter className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1 text-xs font-normal normal-case tracking-normal">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} autoFocus
          className="mb-1 h-7 w-full rounded border border-slate-200 bg-transparent px-2 text-xs outline-none focus:border-brand"
          data-testid={testId ? `${testId}-search` : undefined} />
        <button type="button" onClick={clear} data-testid={testId ? `${testId}-option-all` : undefined}
          className="flex w-full items-center justify-between rounded px-2 py-1.5 hover:bg-slate-100">
          <span>All</span>{!active && <Check className="h-3 w-3 text-brand" />}
        </button>
        <div className="max-h-64 overflow-y-auto">
          {shown.length === 0 && <div className="px-2 py-1.5 text-slate-400">No matches</div>}
          {shown.map((o) => {
            const on = multi ? selected.includes(o.value) : selected === o.value;
            return (
              <button key={o.value} type="button" onClick={() => toggle(o.value)} data-testid={testId ? `${testId}-option-${o.value}` : undefined}
                className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 hover:bg-slate-100">
                <span className="truncate">{o.label}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {o.count != null && <span className="text-slate-400 num">{o.count}</span>}
                  {on && <Check className="h-3 w-3 text-brand" />}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// <th> content: name sorts (click / click again flips); optional filter icon beside it.
export function SortableHeader({ label, sorted, direction, onSort, filter, className = "", testId }) {
  return (
    <span className={`group/th inline-flex items-center gap-1 ${className}`}>
      <button type="button" onClick={onSort} className="inline-flex items-center gap-1 font-semibold hover:text-foreground" data-testid={testId}>
        {label}<SortArrow sortActive={sorted} direction={direction} />
      </button>
      {filter}
    </span>
  );
}
